import { useState } from 'react';

import { Search, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import useAddFactsToChronology from '@/api/mutations/useAddFactsToChronology';
import { useGetChronologies } from '@/api/queries/useGetChronologies';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/new-button';
import Modal from '@/components/molecules/Modals/CustomModal';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/molecules/table';
import { useGeneralPagination } from '@/hooks/useGeneralPagination';

interface SelectChronologyModalProps {
  isOpen: boolean;
  handleClose: () => void;
  factIds: string[];
}

export function SelectChronologyModal({ isOpen, handleClose, factIds }: SelectChronologyModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChronologyId, setSelectedChronologyId] = useState<string | null>(null);
  const { pagination, setPagination } = useGeneralPagination({ pageSize: 20 });

  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const { data: responseChronologies, isFetching: isLoadingChronologies } = useGetChronologies({
    caseId: caseId ?? '',
    searchQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });

  const addFactsMutation = useAddFactsToChronology();

  const handleSubmit = () => {
    if (!selectedChronologyId || !caseId) {
      toast.error('Please select a chronology');
      return;
    }

    addFactsMutation.mutate(
      {
        caseId,
        chronologyId: selectedChronologyId,
        factIds,
      },
      {
        onSuccess: (data) => {
          const addedCount = data?.addedFacts?.length ?? 0;
          const duplicateCount = factIds.length - addedCount;

          if (addedCount === 0) {
            toast.info('All facts already exist in this chronology');
          } else {
            toast.success(
              // sorry this is gross
              `${addedCount} ${addedCount === 1 ? 'fact' : 'facts'} added to chronology ${duplicateCount ? `(${duplicateCount} already ${duplicateCount === 1 ? 'exists' : 'exist'})` : ''}`,
            );
          }
          handleClose();
          setSelectedChronologyId(null);
        },
        onError: () => {
          toast.error('Failed to add facts to chronology');
        },
      },
    );
  };

  const content = (
    <div className="flex flex-col gap-4 p-8">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold">Select Chronology</h2>
        <p className="text-sm text-gray-600">Choose a chronology to add the supporting evidence to</p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder="Search chronologies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      <div className="max-h-96 overflow-y-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead className="whitespace-nowrap">Created Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingChronologies ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center">
                  <Loader2 className="mx-auto animate-spin" />
                </TableCell>
              </TableRow>
            ) : responseChronologies?.chronologies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-gray-500">
                  No chronologies found
                </TableCell>
              </TableRow>
            ) : (
              responseChronologies?.chronologies.map((chronology) => (
                <TableRow
                  key={chronology.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setSelectedChronologyId(chronology.id)}
                >
                  <TableCell>
                    <input
                      type="radio"
                      checked={selectedChronologyId === chronology.id}
                      onChange={() => setSelectedChronologyId(chronology.id)}
                      className="size-4 cursor-pointer"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{chronology.title}</TableCell>
                  <TableCell>{chronology.createdBy?.email ?? 'Unknown'}</TableCell>
                  <TableCell>{new Date(chronology.createdDate).toLocaleDateString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {responseChronologies && responseChronologies.pageCount > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Page {pagination.pageIndex + 1} of {responseChronologies.pageCount}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
              disabled={pagination.pageIndex === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
              disabled={pagination.pageIndex + 1 >= responseChronologies.pageCount}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="secondary" onClick={handleClose} disabled={addFactsMutation.isLoading}>
          Cancel
        </Button>
        <Button variant="brand" onClick={handleSubmit} disabled={!selectedChronologyId || addFactsMutation.isLoading}>
          {addFactsMutation.isLoading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Adding...
            </>
          ) : (
            `Add ${factIds.length} ${factIds.length === 1 ? 'fact' : 'facts'} to Chronology`
          )}
        </Button>
      </div>
    </div>
  );

  return <Modal isOpen={isOpen} handleClose={handleClose} content={content} maxWidth="48rem" />;
}
